import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import './Progress.css';
import './Home.css';
import { BankTitleMap, BankLogoMap, BankList } from "../constants/banks.ts";
import { CertTitleMap, Cert } from "../constants/certs.ts";
import CheckLabel from './Components/CheckLabel.jsx';
import CertButton from './Components/CertButton.jsx';
import NextButton from './Components/NextButton.jsx';
import ProcessStep from './Components/ProcessStep.jsx';
import FloatingButton from './Components/FloatingButton.jsx';
import Arrow from './Components/Arrow.jsx';
import { loginRequest, loginResult, consent } from '../api/repository';

function BankSelect({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative', width: 140, flexShrink: 0 }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', height: 34, padding: '0 8px',
          border: '1.5px solid #DDE1E7', borderRadius: 8,
          backgroundColor: '#FAFBFC', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 6,
          fontSize: 13, fontFamily: 'inherit', color: '#1a2a3a',
        }}
      >
        <img src={BankLogoMap[value]} alt="" style={{ width: 18, height: 18, objectFit: 'contain', flexShrink: 0 }} />
        <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {BankTitleMap[value]}
        </span>
        <span style={{ fontSize: 10, color: '#8a95a0', flexShrink: 0 }}>▾</span>
      </button>
      {open && (
        <ul style={{
          position: 'absolute', top: 38, left: 0, width: '100%', zIndex: 999,
          background: '#fff', border: '1.5px solid #DDE1E7', borderRadius: 8,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: 200,
          overflowY: 'auto', padding: 0, margin: 0, listStyle: 'none',
        }}>
          {BankList.map(bank => (
            <li
              key={bank}
              onClick={() => { onChange(bank); setOpen(false); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 12px', cursor: 'pointer', fontSize: 13,
                color: '#1a2a3a',
                backgroundColor: bank === value ? 'rgba(49,130,246,0.08)' : 'transparent',
              }}
            >
              <img src={BankLogoMap[bank]} alt="" style={{ width: 18, height: 18, objectFit: 'contain', flexShrink: 0 }} />
              {BankTitleMap[bank]}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


const ProcessView = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [suimStep, setSuimStep] = useState(1);
  const [sid, setSid] = useState(null);
  const [retryCnt, setRetryCnt] = useState(0);

  const sidRef = useRef(sid);
  const stepRef = useRef(step);
  const retryCntRef = useRef(retryCnt);

  useEffect(() => {
    retryCntRef.current = retryCnt;
  }, [retryCnt]);

  const [showPopup, setShowPopup] = useState(false);
  const [popupText, setPopupText] = useState("");
  const [showRetry, setShowRetry] = useState(false);
  const [showKakao, setShowKakao] = useState(false);
  const [showError, setShowError] = useState(false);
  const [onConfirmCallback, setOnConfirmCallback] = useState(null);
  const [onRetryCallback, setOnRetryCallback] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const showPopupWithText = ({ text, showRetry = false, showKakao = false, showError = false, onRetry = null, onConfirm = null }) => {
    setPopupText(text);
    setShowPopup(true);
    setShowRetry(showRetry);
    setShowKakao(showKakao);
    setShowError(showError);
    setOnRetryCallback(() => onRetry);
    setOnConfirmCallback(() => onConfirm);
  };

  const [form, setForm] = useState({
    name: "",
    path: "",
    phone: "",
    birth1: "",
    birth2: "",
    birth2Raw: "",
    selectedBank: BankList[0],
    bankAccount: "",
    selectedCert: Cert[0],
    agreeAll: false,
    agree0: false,
    agree1: false,
    agree2: false,
  });

  const pathOptions = ["개별 프리랜서", "기장업체 대표", "기장업체 직원", "기타"];

  const focusRefs = {
    name: useRef(),
    phone: useRef(),
    birth1: useRef(),
    birth2: useRef(),
    bank: useRef()
  };

  // 로그인 결과 확인을 위한 interval 설정
  const pollIntervalRef = useRef(null);
  useEffect(() => {
    sidRef.current = sid;
  }, [sid]);
  useEffect(() => {
    stepRef.current = step;
  }, [step]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSelect = (value) => {
    setForm((prev) => ({ ...prev, path: value }));
  };

  const handleBankSelect = (bank) => {
    setForm((prev) => ({ ...prev, selectedBank: bank }));
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    const numericValue = value.replace(/[^0-9]/g, '');

    if (value.length === 11) {
      focusRefs.birth1.current?.focus();
    }

    setForm((prev) => ({ ...prev, phone: numericValue }));
  };

  const handleBirth1Change = (e) => {
    const value = e.target.value;

    if (value.length === 6) {
      focusRefs.birth2.current?.focus();
    }

    setForm((prev) => ({ ...prev, birth1: value }));
  };

  const handleMaskedBirth2 = (e) => {
    const input = e.target.value;
    const rawPrev = form.birth2Raw || "";

    // 사용자가 새로 입력한 부분 찾기
    const newChar = input.length > form.birth2.length ? input[input.length - 1] : "";

    // 실제값 업데이트
    const newRaw = input.length > form.birth2.length
      ? rawPrev + newChar
      : rawPrev.slice(0, input.length); // 삭제 시 처리

    // 마스킹 처리: 마지막 글자만 보이기
    const masked = newRaw.split("").map((ch, idx) => (idx === newRaw.length - 1 ? ch : "*")).join("");

    if (rawPrev.length === 6) {
      focusRefs.bank.current?.focus();
    }

    setForm((prev) => ({
      ...prev,
      birth2: masked,
      birth2Raw: newRaw,
    }));
  };

  function getBirthFront(birth) {
    const year = birth.substring(0, 2);
    const month = birth.substring(2, 4);
    const day = birth.substring(4, 6);

    let fullYear;
    const yearInt = parseInt(year, 10);

    if (yearInt >= 0 && yearInt <= 24) {
      fullYear = `20${year}`; // 00~24까지는 20##
    } else {
      fullYear = `19${year}`; // 그 외 19##
    }

    return `${fullYear}${month}${day}`;
  }

  const handleKeyDown = (type) => (e) => {
    if (e.key === "Enter") {
      e.preventDefault();

      switch (type) {
        case "name":
          focusRefs.phone.current?.focus();
          break;
        case "phone":
          focusRefs.birth1.current?.focus();
          break;
        case "birth1":
          focusRefs.birth2.current?.focus();
          break;
        case "birth2":
          focusRefs.bank.current?.focus();
          break;
        case "bank":
          focusRefs.bank.current?.blur(); // 마지막엔 포커스 해제
          break;
        default:
          break;
      }
    }
  };

  const canNextStep = form.name && form.path && form.phone.length === 11 && form.birth1.length === 6 && form.birth2.length === 7 && form.bankAccount && form.agree0 && form.agree1 && form.agree2;
  /// API : 간편인증 로그인 요청
  const tapStep1Button = async () => {
    if (isLoading) { return }

    if (step === 1 && canNextStep) {
      console.log("간편인증 로그인 요청")
      const body = {
        name: form.name,
        application_path: form.path,
        birth: getBirthFront(form.birth1),
        phone: form.phone,
        provider: form.selectedCert,
        resi_num: form.birth1 + form.birth2Raw,
        bank: {
          name: BankTitleMap[form.selectedBank],
          account: form.bankAccount,
        },
      };

      setIsLoading(true)
      const result = await loginRequest(body);
      setIsLoading(false)

      if (result.success) {
        console.log("요청 성공")
        setSid(result.sid)
        setStep(2);

        // 2초마다 간편인증 로그인 결과 확인
        pollIntervalRef.current = setInterval(async () => {
          if (stepRef.current === 2) {
            tapStep2Button({ isAuto: true });
          } else {
            clearInterval(pollIntervalRef.current);
          }
        }, 3000);
      } else {
        console.log("요청 실패")
        showPopupWithText({
          text: result.message,
        });
      }
    }
  };

  /// API : 간편인증 로그인 결과
  const tapStep2Button = async ({ isAuto = false }) => {
    if (isLoading) { return }

    console.log("간편인증 로그인 결과 확인 ")

    setIsLoading(true)
    const result = await loginResult(sidRef.current);
    setIsLoading(false)

    if (result.success) {
      console.log("인증 성공")
      console.log(result.data)
      setStep(3);
      setSuimStep(1);

      // 수임동의 API 호출
      clearInterval(pollIntervalRef.current);
      tapStep3Button();

      // 1초 후 2단계로
      setTimeout(() => {
        setSuimStep(2);
      }, 1000);
    } else {
      console.log("인증 실패")
      if (!isAuto) {
        showPopupWithText({
          text: result.message,
        });
      }
    }
  };

  /// API : 수임 동의
  const tapStep3Button = async () => {
    if (isLoading) { return }

    setIsLoading(true)
    const result = await consent(sidRef.current);
    setIsLoading(false)

    if (result.success) {
      setSuimStep(3);
      showPopupWithText({
        text: "[자료 수취 완료]\n아래 신고상담으로 오셔서\n'성함' 말씀주시면 신고절차 안내드리겠습니다",
        showKakao: true,
        onRetry: () => {
          window.open("http://pf.kakao.com/_GCiexb/chat", "_blank");
        },
        onConfirm: () => {
          window.history.back()
        }
      });
    } else {
      console.log("retryCnt", retryCntRef.current);
      if (retryCntRef.current >= 1) {
        setRetryCnt(0);
        showPopupWithText({
          text: result.message || "현재 홈택스 서버가 불안정하여 10분 뒤에 다시 진행해주시면 감사하겠습니다.",
          showError: true,
          onRetry: () => {
            window.open("http://pf.kakao.com/_GCiexb/chat", "_blank");
          },
          onConfirm: () => {
            window.history.back();
          }
        });
      } else {
        showPopupWithText({
          text: result.message,
          showRetry: true,
          onRetry: () => {
            setRetryCnt(retryCntRef.current + 1);
            setSuimStep(1);

            // 5초 후 수임동의 API 호출
            setTimeout(() => {
              tapStep3Button();
            }, 5000);

            // 1초 후 2단계로
            setTimeout(() => {
              setSuimStep(2);
            }, 1000);
          },
          onConfirm: () => {
            setStep(1)
          }
        });
      }
    }
  };

  return (
    <>
      {/* ── 헤더 ── */}
      <div className="progress-header">
        <img src="assets/logo.png" alt="세일택스 로고" className="progress-header-logo" onClick={() => navigate('/home')} style={{ cursor: 'pointer' }} />
      </div>
      <div className="progress-wrapper">
        <div className="progress-content">
          <FloatingButton />


          {/* ── 단계 표시기 ── */}
          <div className="progress-steps">
            {[1, 2, 3].map((n) => (
              <React.Fragment key={n}>
                <div className={`progress-step ${step === n ? "active" : step > n ? "done" : ""}`}>
                  <div className="progress-step-circle">{n}</div>
                  <span className="progress-step-label">{["정보입력", "본인인증", "완료"][n - 1]}</span>
                </div>
                {n < 3 && <div className={`progress-step-line ${step > n ? "done" : ""}`} />}
              </React.Fragment>
            ))}
          </div>

          {/* -------------------- 1단계 -------------------- */}
          {step === 1 && (
            <>
              <div className="progress-card">
                {/* 신고구분 */}
                <div className="field-group">
                  <span className="field-label">신고구분<span className="field-required">*</span></span>
                  <div style={{ display: "flex", gap: "8px", marginTop: "2px", flexWrap: "wrap" }}>
                    {pathOptions.map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => handleSelect(option)}
                        className={`path-chip${form.path === option ? " selected" : ""}`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>

                <FieldInput label="이름" ref={focusRefs.name} onKeyDown={handleKeyDown("name")} value={form.name} onChange={handleChange("name")} />
                <FieldInput label="휴대폰번호" ref={focusRefs.phone} onKeyDown={handleKeyDown("phone")} type="text" inputMode="numeric" value={form.phone} onChange={handlePhoneChange} maxLength={11} />

                {/* 주민등록번호 */}
                <div className="field-group">
                  <span className="field-label">주민등록번호<span className="field-required">*</span></span>
                  <div className="input-container">
                    <input type="text" ref={focusRefs.birth1} onKeyDown={handleKeyDown("birth1")} inputMode="numeric" value={form.birth1} maxLength={6} onChange={handleBirth1Change} className="input" />
                    <span style={{ color: '#9CA3AF', fontSize: 18 }}>—</span>
                    <input type="text" ref={focusRefs.birth2} onKeyDown={handleKeyDown("birth2")} inputMode="numeric" value={form.birth2} maxLength={7} onChange={handleMaskedBirth2} className="input" />
                  </div>
                </div>

                {/* 계좌번호 */}
                <div className="field-group">
                  <span className="field-label">환급계좌<span className="field-required">*</span></span>
                  <div className="flex gap-2" style={{ marginTop: 2, alignItems: 'center' }}>
                    <BankSelect value={form.selectedBank} onChange={handleBankSelect} />
                    <input type="text" ref={focusRefs.bank} onKeyDown={handleKeyDown("bank")} inputMode="numeric" value={form.bankAccount} onChange={handleChange("bankAccount")} className="input2" />
                  </div>
                </div>

                {/* 본인인증 수단 */}
                <div className="field-group">
                  <span className="field-label">본인인증 수단<span className="field-required">*</span></span>
                  <div className="flex mt-2" style={{ gap: 24 }}>
                    {Object.values(Cert).map(cert => (
                      <CertButton
                        key={cert}
                        cert={cert}
                        title={CertTitleMap[cert]}
                        selected={form.selectedCert === cert}
                        onClick={() => setForm(prev => ({ ...prev, selectedCert: cert }))}
                      />
                    ))}
                  </div>
                </div>

                {/* 약관동의 */}
                <div className="field-group">
                  <div className="terms-header">
                    <span className="field-label" style={{ marginBottom: 0 }}>약관동의<span className="field-required">*</span></span>
                    <CheckLabel
                      label="전체동의"
                      checked={form.agree0 && form.agree1 && form.agree2}
                      onChange={() => {
                        const next = !form.agreeAll;
                        setForm(prev => ({
                          ...prev,
                          agreeAll: next,
                          agree0: next,
                          agree1: next,
                          agree2: next
                        }));
                      }}
                    />
                  </div>

                  <div className="terms-section">
                    <div className="terms-row">
                      <CheckLabel label="[필수] 서비스 이용약관" checked={form.agree0} onChange={() => setForm(prev => ({ ...prev, agree0: !prev.agree0 }))} />
                      <a href="https://seiltax.notion.site/1d832c22e04f805392bfcbeea92dee45?pvs=4" className="terms-link-btn" target="_blank" rel="noopener noreferrer">보러가기</a>
                    </div>
                    <div className="terms-row">
                      <CheckLabel label="[필수] 개인정보 처리 방침" checked={form.agree1} onChange={() => setForm(prev => ({ ...prev, agree1: !prev.agree1 }))} />
                      <a href="https://seiltax.notion.site/1d632c22e04f80b0b5f6fe38638f124e?pvs=4" className="terms-link-btn" target="_blank" rel="noopener noreferrer">보러가기</a>
                    </div>
                    <div className="terms-row">
                      <CheckLabel label="[필수] 주민등록번호 입력 동의" checked={form.agree2} onChange={() => setForm(prev => ({ ...prev, agree2: !prev.agree2 }))} />
                      <a href="https://seiltax.notion.site/1d632c22e04f80c89c77dc91d9c34794?pvs=4" className="terms-link-btn" target="_blank" rel="noopener noreferrer">보러가기</a>
                    </div>
                  </div>
                </div>

                <NextButton step={1} enabled={canNextStep} onClick={tapStep1Button} />
              </div>
            </>
          )}

          {/* -------------------- 2단계 -------------------- */}
          {step === 2 && (
            <div className="auth-guide-card">
              <div className="auth-guide-title">인증을 진행해주세요.</div>
              <div className="auth-guide-desc">
                {'입력하신 휴대폰으로 인증 요청 메시지를 보냈습니다.\n메시지를 받지 못했다면 사용자 정보를 확인 후 다시 시도해주세요.'}
              </div>
              <div className="auth-image-wrap">
                <img src={`assets/${form.selectedCert}_cert.png`} alt="인증 이미지" />
              </div>
              <NextButton step={2} enabled={true} onClick={tapStep2Button} />
            </div>
          )}

          {/* -------------------- 3단계 -------------------- */}
          {step === 3 && (
            <div className="process3-wrapper">
              <div className="process3-message">
                {suimStep !== 3 ? "잠시만 기다려주세요.\n최대 1분 정도 소요됩니다." : "수임동의가 완료되었습니다!"}
              </div>
              <div className="process3-steps">
                <ProcessStep step={1} text="요청중" isActive={suimStep === 1} />
                <Arrow />
                <ProcessStep step={2} text="처리중" isActive={suimStep === 2} />
                <Arrow />
                <ProcessStep step={3} text="완료" isActive={suimStep === 3} />
              </div>
            </div>
          )}
        </div>

        {/* ── 팝업 ── */}
        {showPopup && (
          <div className="popup-overlay">
            <div className="popup-content">
              {showKakao && (
                <p style={{ whiteSpace: "pre-wrap", lineHeight: 1.8 }}>
                  🎉 <strong>자료 수취가 완료</strong>되었어요!{"\n\n"}아래 카카오톡 <strong>신고상담</strong>으로 오셔서{"\n"}<strong>성함</strong>을 말씀주시면{"\n"}세금 신고 절차를 친절하게 안내드릴게요 😊
                </p>
              )}
              {showError && (
                <p style={{ whiteSpace: "pre-wrap", lineHeight: 1.7 }}>
                  {"홈택스 서버가 일시적으로 불안정해서\n자동 처리가 어려운 상황이에요 😥\n\n아래 카카오톡으로 오셔서\n"}<strong>"홈택스 에러가 떠서요"</strong>{" 와 함께\n"}<strong>성함</strong>{"을 알려주시면\n빠르고 친절하게 도와드릴게요! 🙏"}
                </p>
              )}
              {!showKakao && !showError && (
                <p style={{ whiteSpace: "pre-wrap" }}>{popupText}</p>
              )}

              <div className="popup-buttons">
                {!showRetry && !showKakao && !showError && (
                  <button onClick={() => { setShowPopup(false); if (onConfirmCallback) onConfirmCallback(); }} className="popup-button confirm">확인</button>
                )}
                {(showRetry || showKakao || showError) && (
                  <button onClick={() => { setShowPopup(false); if (onConfirmCallback) onConfirmCallback(); }} className="popup-button reset">처음으로</button>
                )}
                {showRetry && onRetryCallback && (
                  <button onClick={() => { setShowPopup(false); onRetryCallback(); }} className="popup-button retry">재시도</button>
                )}
                {(showKakao || showError) && onRetryCallback && (
                  <button onClick={() => { setShowPopup(false); onRetryCallback(); }} className="popup-button kakao">
                    <img src="assets/kakaotalk.png" alt="카카오톡" width={17} height={17} />
                    신고상담
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── 푸터 ── */}
      <div className="home-footer">
        <div className="home-footer-inner">
          <img src="assets/logo.png" alt="세일택스 로고" className="home-footer-logo" />
          <div className="home-footer-info">
            Tel. 02-6953-3971&nbsp;&nbsp;|&nbsp;&nbsp;
            E-mail : info@wtoptax.com&nbsp;&nbsp;|&nbsp;&nbsp;
            서울특별시 강남구 테헤란로 8길 33 2층(청원빌딩)&nbsp;&nbsp;|&nbsp;&nbsp;
            419-81-03476
          </div>
          <div className="home-footer-copy">
            Copyright(C) 2022 세무법인 세일택스 All Right Reserved.&nbsp;&nbsp;
            <a href="https://seiltax.notion.site/1d832c22e04f805392bfcbeea92dee45?pvs=4" className="footer-terms-link" target="_blank" rel="noopener noreferrer">
              이용약관
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

const FieldInput = React.forwardRef(({ label, ...props }, ref) => (
  <div className="field-group">
    <span className="field-label">{label}<span className="field-required">*</span></span>
    <input ref={ref} {...props} className="input" />
  </div>
));

export default ProcessView;
